import type { PreInterviewOnCard, PreInterviewStatus } from '@restworld/ui-components';
import type { JobApplication } from '@restworld/utility-types';

/**
 * Everything the UI needs about the interview, on the card and on the detail timeline.
 * A superset of what the card reads, so the same object can be handed to both.
 */
export type PreInterview = PreInterviewOnCard & {
  /** The chat to reread, once the interview is closed. */
  transcriptHref?: string;
};

/** The chat for this application. Addressed by job position, like the rest of `/candidature`. */
const chatHref = (jobPositionId: string) => `/candidature/${jobPositionId}/intervista`;

/**
 * The three states the card and the timeline can draw. `closed` — the candidate withdrew — is
 * deliberately not among them: the UI has no state for it, and an application whose candidate
 * withdrew is already drawn by the "non interessato" branch of the timeline.
 */
const DRAWN_STATUSES: readonly string[] = ['pending', 'completed', 'expired'] satisfies PreInterviewStatus[];

/**
 * The interview with Serena for one application, or `undefined` when it has none — and
 * `undefined` is the page exactly as it was before any of this existed: no interview step, no
 * button, nothing moved.
 *
 * Two fields decide it, and both come from the application itself. `assessment_channel` says
 * whether the interview lives in the app at all; every application that predates the rollout
 * has it empty, so they all take the `undefined` branch with no special case of their own.
 * `assessment_status` says what state it is in. It is served on **both** worker payloads: the
 * single application computes it after loading, and the list gets it from a join on three
 * columns — never a preload of the conversation, which would carry the interview transcript
 * and the AI's notes to the candidate's browser. When it is absent anyway, this returns
 * `undefined` rather than guessing a state.
 */
export const getPreInterview = (application?: Partial<JobApplication>): PreInterview | undefined => {
  if (application?.assessment_channel !== 'in_app') return undefined;

  const status = application.assessment_status;
  if (!status || !DRAWN_STATUSES.includes(status)) return undefined;

  const jobPositionId = application.job_position_id;
  if (!jobPositionId) return undefined;

  const href = chatHref(jobPositionId);

  // While it is pending the chat is something to finish; once it is closed the same page is
  // the transcript, which is why one address serves both and only the label changes.
  return status === 'pending' ? { status, href } : { status: status as PreInterviewStatus, transcriptHref: href };
};
